/**
 * Phase 4 Task 12 — supervisor dashboard event wiring.
 *
 * Mirrors `wireSocketEvents` in `./events.ts` but only attaches the
 * `supervisor:*` listeners that fan into `useSupervisorStore`. The
 * dashboard mounts call `wireSupervisorEvents(socket)` on mount and
 * invoke the returned cleanup on unmount; the supervisor room join /
 * subscribe is handled by the dashboard component itself so this
 * helper is purely listener bookkeeping.
 */
import { useSupervisorStore } from '../store/supervisorStore.js'
import { useUiStore } from '../store/uiStore.js'
import type { GatewaySocket } from './client.js'
import type { ServerToClientEvents } from './types.js'

export function wireSupervisorEvents(socket: GatewaySocket): () => void {
  const sup = useSupervisorStore.getState()
  const ui = useUiStore.getState()

  const onAgentsSnapshot: ServerToClientEvents['supervisor:agents_snapshot'] = (
    agents,
  ) => {
    sup.setAgentList(agents)
  }
  const onAgentUpdate: ServerToClientEvents['supervisor:agent_update'] = (
    agent,
  ) => {
    sup.updateAgent(agent)
  }
  const onCallsSnapshot: ServerToClientEvents['supervisor:calls_snapshot'] = (
    calls,
  ) => {
    sup.setLiveCalls(calls)
  }
  const onAlert: ServerToClientEvents['supervisor:alert'] = (data) => {
    sup.addAlert(data)
  }
  const onError: ServerToClientEvents['supervisor:error'] = (data) => {
    ui.addNotification(`Supervisor: ${data.message}`, 'error')
  }

  socket.on('supervisor:agents_snapshot', onAgentsSnapshot)
  socket.on('supervisor:agent_update', onAgentUpdate)
  socket.on('supervisor:calls_snapshot', onCallsSnapshot)
  socket.on('supervisor:alert', onAlert)
  socket.on('supervisor:error', onError)

  return () => {
    socket.off('supervisor:agents_snapshot', onAgentsSnapshot)
    socket.off('supervisor:agent_update', onAgentUpdate)
    socket.off('supervisor:calls_snapshot', onCallsSnapshot)
    socket.off('supervisor:alert', onAlert)
    socket.off('supervisor:error', onError)
  }
}
